import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import slugify from 'react-slugify'

import { FirebaseContext } from './context'
import { deleteScreenComponentsVariables, GetVariable, Update } from './hooks'
import { BrandingThemeType, Config, getScreen, Screen } from './utils'

interface Props {
  inDevice: boolean
  language: string
  theme: BrandingThemeType
  desktopMode: boolean
  config: Config
  screens: Screen[]
  page: string
  launchOpen: boolean
  setLaunchOpen: React.Dispatch<React.SetStateAction<boolean>>
  navigate: (pathname: string) => void
  navigateBack: () => void
  closeDialog: () => Promise<void>
  closeAllDialogs: () => void
  getVariable: GetVariable
  update: Update
}

export const Dialog: React.FC<Props> = ({
  inDevice,
  language,
  theme,
  desktopMode,
  config,
  screens,
  page,
  launchOpen,
  setLaunchOpen,
  navigate,
  navigateBack,
  closeDialog,
  closeAllDialogs,
  getVariable,
  update,
}) => {
  const screenParentElementRef = useRef<HTMLDivElement>(null)
  const { f } = useContext(FirebaseContext)
  const [dialogInDialog, setDialogInDialog] = useState<{
    page: string
    beforeClose?: () => Promise<void>
  } | null>(null)

  const screen = useMemo(() => screens.find(el => slugify(el.screenName) === page), [page])

  const openDialogInDialog = (page: string, beforeClose?: () => Promise<void>) =>
    setDialogInDialog({ page, beforeClose })

  const closeDialogInDialog = async () => {
    if (dialogInDialog?.beforeClose) {
      await dialogInDialog.beforeClose()
    }
    setDialogInDialog(null)
  }

  useEffect(() => {
    const screenParentElement = screenParentElementRef.current
    if (screenParentElement && screen) {
      getScreen(
        f,
        screen,
        config,
        screenParentElement,
        language,
        theme,
        desktopMode,
        launchOpen,
        setLaunchOpen,
        navigate,
        navigateBack,
        openDialogInDialog,
        closeDialog,
        closeAllDialogs,
        getVariable,
        update
      )
    }
  }, [screen, inDevice, language, theme, desktopMode])

  useEffect(() => {
    return () => {
      if (screen) {
        deleteScreenComponentsVariables(screen.id)
      }
    }
  }, [screen])

  return (
    <>
      <div className="dialog" ref={screenParentElementRef} />
      {dialogInDialog && (
        <Dialog
          inDevice={inDevice}
          language={language}
          theme={theme}
          desktopMode={desktopMode}
          config={config}
          screens={screens}
          page={dialogInDialog.page}
          navigate={navigate}
          navigateBack={navigateBack}
          launchOpen={launchOpen}
          setLaunchOpen={setLaunchOpen}
          closeDialog={closeDialogInDialog}
          closeAllDialogs={closeAllDialogs}
          getVariable={getVariable}
          update={update}
        />
      )}
    </>
  )
}
